import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Loader2, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { adminApi, adminToken, aErr } from "@/admin/adminApi";

const MSG = {
  invalid_credentials: "E-mail ou mot de passe incorrect.",
  invalid_code: "Code invalide.",
  too_many_attempts: "Trop de tentatives, réessayez dans 15 min.",
  weak_password_admin: "12 caractères minimum, avec lettres et chiffres.",
  password_reused: "Choisissez un mot de passe différent.",
  invalid_token: "Session expirée, recommencez.",
};

export default function AdminLogin() {
  const navigate = useNavigate();
  const [step, setStep] = useState({ stage: "login" });
  const [f, setF] = useState({ email: "", password: "", code: "", pw: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      let r;
      if (step.stage === "login") r = await adminApi.post("/auth/login", { email: f.email, password: f.password });
      else if (step.stage === "change_password") r = await adminApi.post("/auth/change-password", { token: step.token, new_password: f.pw });
      else r = await adminApi.post("/auth/2fa", { token: step.token, code: f.code });
      if (r.data.stage === "done") {
        adminToken.set(r.data.access_token);
        navigate("/admin");
      } else setStep(r.data);
    } catch (ex) {
      const d = aErr(ex);
      setErr(MSG[d] || d);
      if (d === "invalid_token") setStep({ stage: "login" });
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "h-12 rounded-xl border-white/10 bg-white/5";
  return (
    <div className="grid min-h-screen place-items-center bg-[#0B0D12] px-4 text-slate-100" data-testid="admin-login-page">
      <form onSubmit={submit} className="surface w-full max-w-sm space-y-4 p-6">
        <div className="flex items-center gap-2 font-display text-xl font-bold"><ShieldCheck className="h-6 w-6 text-amber-400" /> Administration</div>
        {step.stage === "login" && (<>
          <Input placeholder="E-mail" type="email" value={f.email} onChange={set("email")} className={inputCls} data-testid="admin-email-input" required />
          <Input placeholder="Mot de passe" type="password" value={f.password} onChange={set("password")} className={inputCls} data-testid="admin-password-input" required />
        </>)}
        {step.stage === "setup_2fa" && (
          <div className="space-y-3 text-sm text-slate-300" data-testid="admin-2fa-setup">
            <p>Scannez ce QR code avec une application d'authentification (Google Authenticator, Microsoft Authenticator…), puis saisissez le code à 6 chiffres.</p>
            <div className="mx-auto w-fit rounded-xl bg-white p-3"><QRCodeSVG value={step.otpauth_uri} size={170} /></div>
            <p className="break-all text-center font-mono text-xs text-slate-500" data-testid="admin-2fa-secret">{step.secret}</p>
          </div>
        )}
        {(step.stage === "setup_2fa" || step.stage === "verify_2fa") && (
          <Input placeholder="Code à 6 chiffres" inputMode="numeric" value={f.code} onChange={set("code")} className={`${inputCls} text-center font-mono text-xl tracking-widest`} data-testid="admin-2fa-code-input" required />
        )}
        {step.stage === "change_password" && (<>
          <p className="text-sm text-slate-300">Première connexion : choisissez un nouveau mot de passe (12 caractères minimum, lettres et chiffres).</p>
          <Input placeholder="Nouveau mot de passe" type="password" value={f.pw} onChange={set("pw")} className={inputCls} data-testid="admin-new-password-input" required />
        </>)}
        {err && <p className="text-sm text-red-300" data-testid="admin-login-error">{err}</p>}
        <button disabled={busy} className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-amber-500 font-semibold text-[#0B0D12] disabled:opacity-60" data-testid="admin-login-submit">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Continuer
        </button>
      </form>
    </div>
  );
}
